import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Card,
  CardBody,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Trophy, Timer, Cube, ArrowRight, Check, Camera, Bluetooth, Flower } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { LatestSolves } from '../components/LatestSolves'
import { PageMeta } from '../components/PageMeta'

const Home = () => {
  const { t } = useTranslation()

  return (
    <Container maxW="container.xl" py={10}>
      <PageMeta title="home.pageTitle" description="home.pageDescription" />
      <VStack spacing={16} align="stretch">
        {/* Hero Section with Latest Solves */}
        <VStack spacing={12} align="stretch">
          <VStack spacing={8} textAlign="center">
            <Heading
              size="2xl"
              variant="gradient"
              mb={4}
            >
              {t('home.welcome')}
            </Heading>
            <Text fontSize="xl" color="text.muted" maxW="2xl">
              {t('home.subtitle')}
            </Text>
          </VStack>

          {/* Latest Solves right below the welcome message */}
          <Box>
            <LatestSolves />
          </Box>
        </VStack>

        {/* Featured Tools Section */}
        <VStack spacing={8} align="stretch">
          <Heading size="lg" textAlign="center" color="text.default">
            {t('home.featuredTools.title')}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {/* Cube Solver Card */}
            <Card
              variant="filled"
              bg={useColorModeValue('white', 'keymash.bg.elevated')}
              borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
              boxShadow="md"
            >
              <CardBody>
                <VStack spacing={6} align="flex-start">
                  <Box
                    p={3}
                    bg={useColorModeValue('teal.50', 'keymash.bg.subtle')}
                    color="teal.500"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={useColorModeValue('teal.200', 'keymash.border.subtle')}
                  >
                    <Camera size={32} weight="duotone" />
                  </Box>
                  <VStack align="flex-start" spacing={4}>
                    <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                      {t('home.heroCards.cubeSolver.title')}
                    </Heading>
                    <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                      <ListItem>
                        <ListIcon as={Check} color="teal.500" />
                        {t('home.heroCards.cubeSolver.feature1')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="teal.500" />
                        {t('home.heroCards.cubeSolver.feature2')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="teal.500" />
                        {t('home.heroCards.cubeSolver.feature3')}
                      </ListItem>
                    </List>
                  </VStack>
                  <Button
                    as={RouterLink}
                    to="/solve"
                    variant="keymash"
                    rightIcon={<ArrowRight weight="bold" />}
                    size="lg"
                    width="full"
                    colorScheme="teal"
                  >
                    {t('home.heroCards.cubeSolver.button')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            {/* Bluetooth Timer Card */}
            <Card
              variant="filled"
              bg={useColorModeValue('white', 'keymash.bg.elevated')}
              borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
              boxShadow="md"
            >
              <CardBody>
                <VStack spacing={6} align="flex-start">
                  <Box
                    p={3}
                    bg={useColorModeValue('blue.50', 'keymash.bg.subtle')}
                    color="blue.500"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={useColorModeValue('blue.200', 'keymash.border.subtle')}
                  >
                    <Bluetooth size={32} weight="duotone" />
                  </Box>
                  <VStack align="flex-start" spacing={4}>
                    <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                      {t('home.heroCards.bluetooth.title')}
                    </Heading>
                    <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                      <ListItem>
                        <ListIcon as={Check} color="blue.500" />
                        {t('home.heroCards.bluetooth.feature1')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="blue.500" />
                        {t('home.heroCards.bluetooth.feature2')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="blue.500" />
                        {t('home.heroCards.bluetooth.feature3')}
                      </ListItem>
                    </List>
                  </VStack>
                  <Button
                    as={RouterLink}
                    to="/timer"
                    variant="keymash"
                    rightIcon={<ArrowRight weight="bold" />}
                    size="lg"
                    width="full"
                    colorScheme="blue"
                  >
                    {t('home.heroCards.bluetooth.button')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            {/* Zen Mode Card */}
            <Card
              variant="filled"
              bg={useColorModeValue('white', 'keymash.bg.elevated')}
              borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
              boxShadow="md"
            >
              <CardBody>
                <VStack spacing={6} align="flex-start">
                  <Box
                    p={3}
                    bg={useColorModeValue('purple.50', 'keymash.bg.subtle')}
                    color="purple.500"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={useColorModeValue('purple.200', 'keymash.border.subtle')}
                  >
                    <Flower size={32} weight="duotone" />
                  </Box>
                  <VStack align="flex-start" spacing={4}>
                    <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                      {t('home.heroCards.zenMode.title')}
                    </Heading>
                    <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                      <ListItem>
                        <ListIcon as={Check} color="purple.500" />
                        {t('home.heroCards.zenMode.feature1')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="purple.500" />
                        {t('home.heroCards.zenMode.feature2')}
                      </ListItem>
                      <ListItem>
                        <ListIcon as={Check} color="purple.500" />
                        {t('home.heroCards.zenMode.feature3')}
                      </ListItem>
                    </List>
                  </VStack>
                  <Button
                    as={RouterLink}
                    to="/timer/zen"
                    variant="keymash"
                    rightIcon={<ArrowRight weight="bold" />}
                    size="lg"
                    width="full"
                    colorScheme="purple"
                  >
                    {t('home.heroCards.zenMode.button')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>

        {/* Learning Paths Section */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {/* LBL Method Card */}
          <Card
            variant="filled"
            bg={useColorModeValue('white', 'keymash.bg.elevated')}
            borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
            boxShadow="md"
          >
            <CardBody>
              <VStack spacing={6} align="flex-start">
                <Box
                  p={3}
                  bg={useColorModeValue('gray.50', 'keymash.bg.subtle')}
                  color="brand.500"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
                >
                  <Cube size={32} weight="duotone" />
                </Box>
                <VStack align="flex-start" spacing={4}>
                  <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                    {t('home.heroCards.lbl.title')}
                  </Heading>
                  <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.lbl.feature1')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.lbl.feature2')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.lbl.feature3')}
                    </ListItem>
                  </List>
                </VStack>
                <Button
                  as={RouterLink}
                  to="/beginners-method"
                  variant="keymash"
                  rightIcon={<ArrowRight weight="bold" />}
                  size="lg"
                  width="full"
                >
                  {t('home.heroCards.lbl.button')}
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* CFOP Method Card */}
          <Card
            variant="filled"
            bg={useColorModeValue('white', 'keymash.bg.elevated')}
            borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
            boxShadow="md"
          >
            <CardBody>
              <VStack spacing={6} align="flex-start">
                <Box
                  p={3}
                  bg={useColorModeValue('gray.50', 'keymash.bg.subtle')}
                  color="keymash.accent.purple"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
                >
                  <Trophy size={32} weight="duotone" />
                </Box>
                <VStack align="flex-start" spacing={4}>
                  <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                    {t('home.heroCards.cfop.title')}
                  </Heading>
                  <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.cfop.feature1')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.cfop.feature2')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.cfop.feature3')}
                    </ListItem>
                  </List>
                </VStack>
                <Button
                  as={RouterLink}
                  to="/cfop"
                  variant="keymash"
                  rightIcon={<ArrowRight weight="bold" />}
                  size="lg"
                  width="full"
                >
                  {t('home.heroCards.cfop.button')}
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Timer Card */}
          <Card
            variant="filled"
            bg={useColorModeValue('white', 'keymash.bg.elevated')}
            borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
            boxShadow="md"
          >
            <CardBody>
              <VStack spacing={6} align="flex-start">
                <Box
                  p={3}
                  bg={useColorModeValue('gray.50', 'keymash.bg.subtle')}
                  color="keymash.accent.pink"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={useColorModeValue('gray.200', 'keymash.border.subtle')}
                >
                  <Timer size={32} weight="duotone" />
                </Box>
                <VStack align="flex-start" spacing={4}>
                  <Heading size="lg" color={useColorModeValue('gray.800', 'text.default')}>
                    {t('home.heroCards.timer.title')}
                  </Heading>
                  <List spacing={3} color={useColorModeValue('gray.600', 'text.muted')}>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.timer.feature1')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.timer.feature2')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={Check} color="green.500" />
                      {t('home.heroCards.timer.feature3')}
                    </ListItem>
                  </List>
                </VStack>
                <Button
                  as={RouterLink}
                  to="/timer"
                  variant="keymash"
                  rightIcon={<ArrowRight weight="bold" />}
                  size="lg"
                  width="full"
                >
                  {t('home.heroCards.timer.button')}
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Container>
  )
}

export default Home
